import { ChevronRight } from "@mui/icons-material";
import cx from "classnames";
import { type FC, useMemo } from "react";
import NoSSR from "react-no-ssr";

import { type DocumentMetadata, makeUrlPrefix } from "@moment/api-collab/api-types";

import { useAuth } from "~/auth/useAuth";
import { LoginButton } from "~/components/topbar/LoginButton";
import { Profile } from "~/components/topbar/Profile";
import { LinkButton } from "~/ui/Button";
import { ThemeSwitcher } from "~/ui/ThemeSwitcher";

export const Header: FC<{
	workspaceName: string;
	documentMetadata: DocumentMetadata;
}> = ({ workspaceName, documentMetadata }) => {
	const hasTitle = !!documentMetadata.title;

	const { isAuthenticated } = useAuth();

	const urlPrefix = useMemo(
		() => makeUrlPrefix(workspaceName, documentMetadata.id, documentMetadata.publishSettings),
		[workspaceName, documentMetadata.id, documentMetadata.publishSettings]
	);

	return (
		<div className={cx("flex items-center gap-2 -ml-3 bg-primary flex-shrink-0 h-16")}>
			<LinkButton preset="quaternary" href={urlPrefix}>
				<div
					className={cx("text-base font-bold", {
						"text-tertiary italic": !hasTitle,
					})}
				>
					{hasTitle ? documentMetadata.title : "Untitled document"}
				</div>
			</LinkButton>
			<div className="ml-auto flex items-center gap-2">
				{documentMetadata.publishSettings?.topbarLinks
					?.filter((link) => link != null)
					.map(({ name, url }) => (
						<LinkButton
							key={`${name}-${url}`}
							preset="quaternary"
							href={url}
							target="_blank"
						>
							{name}
						</LinkButton>
					))}
				{documentMetadata.publishSettings?.topbarCtaButton && (
					<LinkButton
						preset="primary"
						href={documentMetadata.publishSettings?.topbarCtaButton.url ?? "#"}
						target="_blank"
						borderRadius="full"
						endIcon={<ChevronRight />}
					>
						{documentMetadata.publishSettings.topbarCtaButton.name}
					</LinkButton>
				)}

				<NoSSR>{isAuthenticated ? <Profile /> : <LoginButton />}</NoSSR>
				<ThemeSwitcher />
			</div>
		</div>
	);
};
